import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
// components
import Iconify from '../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../components/hook-form';
// utils
import { errorHandler } from '../../utils/errorHandler';
import { track } from '../../components-custom/Analytics';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);

  const FormSchema = Yup.object().shape({
    email: Yup.string().email(translate('auth.login.schema.emailValid')).required(translate('auth.login.schema.email')),
    password: Yup.string()
      .required(translate('auth.login.schema.password'))
      .min(8, translate('auth.login.schema.passwordValid.long'))
      .matches(/(?=.*[A-Z])/, translate('auth.login.schema.passwordValid.uppercase'))
      .matches(/(?=.*[a-z])/, translate('auth.login.schema.passwordValid.lowercase'))
      .matches(/(?=.*[0-9])/, translate('auth.login.schema.passwordValid.digit'))
      .matches(/(?=.*[^A-Za-z0-9])/, translate('auth.login.schema.passwordValid.special')),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
      enqueueSnackbar(translate('auth.login.message.success'), {
        variant: 'success',
      });
      track.event('Login.success');
    } catch (error) {
      console.error(error);
      track.event('Login.error');
      enqueueSnackbar(errorHandler(error, translate('auth.login.message.error')), { variant: 'error' });
      reset();
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <RHFTextField name="email" label={translate('auth.login.email')} />

        <RHFTextField
          name="password"
          label={translate('auth.login.password')}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label={translate('auth.login.rememberMe')} />
        <Link component={RouterLink} variant="subtitle2" to={PATH.auth.forgotPassword}>
          {translate('auth.login.forgotPassword')}
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {translate('auth.login.submit')}
      </LoadingButton>
    </FormProvider>
  );
}
